// noinspection DuplicatedCode
import React, { useCallback, useEffect, useMemo, useState } from "react";
import "react-toastify/dist/ReactToastify.css";

import { Colors } from "../../../assets/Colors";
import { fetchGet } from "../../../fetchers";
import { useMutation } from "@tanstack/react-query";
import { Card } from "@material-tailwind/react";
import { HrdInputGenerator } from "../../../components/form/HrdInputGenerator";
import { useForm } from "../../../components/isRequired";
import { SearchBarSmallV2 } from "../../../components/form/SearchBar";
import Skeltons from "../../../components/loading/Skeltons";
import { downloadExcel } from "react-export-table-to-excel";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { useNavigate } from "react-router-dom";

const PAGE_SIZE = 10;

const KasBankPage = () => {
  const navigate = useNavigate();
  const initialState = { BisnisUnitID: "ALL", Bulan: new Date() };
  const { values, errors, touched, customHandler } = useForm(initialState, []);

  // Filter yang sudah diterapkan (via tombol "Buka"). Bulan null = tampilkan semua periode.
  const [applied, setApplied] = useState({ BisnisUnitID: "ALL", Bulan: null });
  const [page, setPage] = useState(1);
  const [searchInput, setSearchInput] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedBagian, setSelectedBagian] = useState(""); // "" = semua, "__none__" = tanpa Bagian

  // Data lintas unit (endpoint memfilter BisnisUnitID LIKE '%U%' = semua). Filter unit,
  // periode, dan pencarian dilakukan client-side.
  const mutationData = useMutation(() =>
    fetchGet("Accounting/CashJournalget/NonPO/U")
  );

  useEffect(() => {
    mutationData.mutate();
  }, []);

  // fetchGet mengembalikan body penuh {status, message, data:[...]}, array ada di .data.data.
  const allRows = useMemo(() => {
    const d = mutationData.data?.data;
    return Array.isArray(d) ? d : [];
  }, [mutationData.data]);

  const fmtRp = (v) =>
    Math.round(parseFloat(v || 0)).toLocaleString("id-ID", { maximumFractionDigits: 0 });
  const fmtTgl = (v) => (v ? new Date(v).toLocaleDateString("id-ID") : "-");

  // Opsi + jumlah untuk dropdown Bagian (kebanyakan transaksi Uang Muka belum berisi Bagian).
  const bagianInfo = useMemo(() => {
    const m = new Map();
    let none = 0;
    allRows.forEach((r) => {
      if (r.NamaBagian) m.set(r.NamaBagian, (m.get(r.NamaBagian) || 0) + 1);
      else none++;
    });
    return {
      list: [...m.entries()].sort((a, b) => a[0].localeCompare(b[0])),
      none,
    };
  }, [allRows]);

  // ── Filter + sort (client-side) ─────────────────────────────
  const filtered = useMemo(() => {
    let rows = [...allRows];

    // Filter Bisnis Unit
    if (applied.BisnisUnitID && applied.BisnisUnitID !== "ALL") {
      rows = rows.filter((r) => r.BisnisUnitID === applied.BisnisUnitID);
    }

    // Filter Periode (bulan + tahun)
    if (applied.Bulan) {
      const m = new Date(applied.Bulan).getMonth();
      const y = new Date(applied.Bulan).getFullYear();
      rows = rows.filter((r) => {
        if (!r.Tanggal) return false;
        const d = new Date(r.Tanggal);
        return d.getMonth() === m && d.getFullYear() === y;
      });
    }

    // Filter Bagian (instant, client-side). "__none__" = tanpa Bagian.
    if (selectedBagian === "__none__") {
      rows = rows.filter((r) => !r.NamaBagian);
    } else if (selectedBagian) {
      rows = rows.filter((r) => r.NamaBagian === selectedBagian);
    }

    // Pencarian
    const kw = searchQuery.trim().toLowerCase();
    if (kw) {
      rows = rows.filter((r) =>
        [r.CashJournalID, r.DariKepada, r.JenisTransaksi, r.Keterangan, r.NamaBagian, r.Status]
          .some((v) => v && v.toString().toLowerCase().includes(kw))
      );
    }

    // Urutkan: terbaru di atas (Tanggal desc, lalu ID desc)
    rows.sort((a, b) => {
      const ta = a.Tanggal ? new Date(a.Tanggal).getTime() : 0;
      const tb = b.Tanggal ? new Date(b.Tanggal).getTime() : 0;
      if (tb !== ta) return tb - ta;
      return (b.CashJournalID || "").localeCompare(a.CashJournalID || "");
    });
    return rows;
  }, [allRows, applied, searchQuery, selectedBagian]);

  // ── Pagination ─────────────────────────────────────────────
  const totalPage = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPage);
  const pageRows = useMemo(() => {
    const start = (safePage - 1) * PAGE_SIZE;
    return filtered.slice(start, start + PAGE_SIZE);
  }, [filtered, safePage]);

  useEffect(() => {
    setPage(1);
  }, [applied, searchQuery, selectedBagian]);

  // ── Handlers ───────────────────────────────────────────────
  const handleSearchChange = useCallback((e) => {
    const val = typeof e === "string" ? e : e?.target?.value || "";
    setSearchInput(val);
    setSearchQuery(val);
  }, []);

  const filterApi = () => {
    setApplied({ BisnisUnitID: values.BisnisUnitID, Bulan: values.Bulan || null });
  };

  const resetPeriode = () => {
    setApplied({ BisnisUnitID: values.BisnisUnitID, Bulan: null });
  };

  const tambah = () => navigate("/finance/transaction/form-kas-bank");
  const editRow = (id) =>
    navigate("/finance/transaction/form-kas-bank/" + id.toString().replaceAll("/", "-"));
  const kirimRow = (id) =>
    navigate("/finance/transaction/form-kas-bank/" + id.toString().replaceAll("/", "-") + "/true");

  // ── Export ─────────────────────────────────────────────────
  const exportHeader = [
    "Kas Bank ID", "Kepada", "Bagian", "Jenis Transaksi", "Tanggal", "Keterangan", "Nominal", "Status",
  ];
  const exportBody = () =>
    filtered.map((r) => [
      r.CashJournalID || "",
      r.DariKepada || "",
      r.NamaBagian || "",
      r.JenisTransaksi || "",
      fmtTgl(r.Tanggal),
      r.Keterangan || "",
      fmtRp(r.Nominal),
      r.Status || "",
    ]);

  const handleDownloadExcel = () => {
    downloadExcel({
      fileName: "Kas_Bank",
      sheet: "data",
      tablePayload: { header: exportHeader, body: exportBody() },
    });
  };

  const handleDownloadPDF = () => {
    const doc = new jsPDF({ orientation: "landscape" });
    doc.setFontSize(12);
    doc.text("Kas Bank", 14, 14);
    autoTable(doc, {
      head: [exportHeader],
      body: exportBody(),
      startY: 20,
      styles: { fontSize: 7 },
      headStyles: { fillColor: [41, 128, 185], textColor: 255, fontStyle: "bold" },
    });
    doc.save("Kas_Bank.pdf");
  };

  const statusClass = (s) =>
    s === "Approved"
      ? "text-green-600 font-medium"
      : s === "Menunggu Approval"
      ? "text-yellow-600 font-medium"
      : s === "Ditolak"
      ? "text-red-600 font-medium"
      : "text-gray-600";

  const colHeader = [
    "Kas Bank ID", "Kepada", "Bagian", "Jenis Transaksi", "Tanggal", "Keterangan", "Nominal", "Status", "Actions",
  ];

  return (
    <div className="md:ml-64">
      <div style={{ background: Colors.primary }} className="pt-14 pb-16 px-3 md:px-8 h-auto">
        <div className="container mx-auto max-w-full" />
      </div>
      <div className="px-3 md:px-8 h-auto -mt-32">
        <div className="container mx-auto max-w-full">
          <div className="grid grid-cols-1 px-4">
            <Card>
              {/* ── Filter row ── */}
              <div className="flex flex-row flex-wrap items-start justify-start w-full px-4 py-3 gap-4">
                <div className="w-full sm:w-1/4">
                  <HrdInputGenerator
                    type={"BisnisUnit"}
                    name={"BisnisUnitID"}
                    title={"Bisnis Unit"}
                    value={values}
                    includeAll={true}
                    onChange={customHandler}
                    touched={touched}
                    errors={errors}
                    size={"full"}
                  />
                </div>
                <div className="w-full sm:w-1/4">
                  <HrdInputGenerator
                    type={"bulan"}
                    name={"Bulan"}
                    title={"Periode"}
                    value={values}
                    onChange={customHandler}
                    touched={touched}
                    errors={errors}
                    size={"full"}
                  />
                </div>
                <div>
                  <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2">
                    &nbsp;
                  </label>
                  <div className="flex flex-row gap-2">
                    <button
                      type="button"
                      onClick={filterApi}
                      className="h-[42px] text-main-100 hover:text-white border border-main-100 hover:bg-main-100 font-medium rounded-lg text-sm px-7 text-center"
                    >
                      Buka
                    </button>
                    <button
                      type="button"
                      onClick={resetPeriode}
                      className="h-[42px] text-gray-600 hover:text-white border border-gray-400 hover:bg-gray-400 font-medium rounded-lg text-sm px-5 text-center"
                    >
                      Semua Periode
                    </button>
                  </div>
                </div>
              </div>

              {mutationData.isLoading ? (
                <div className="w-full pt-0">
                  <Skeltons />
                </div>
              ) : (
                <>
                  {/* ── Toolbar: Tambah / Export / Search ── */}
                  <div className="flex w-full justify-between px-4 pt-2 flex-col sm:flex-row mt-2 gap-2">
                    <div className="flex flex-row gap-2 flex-wrap">
                      <button
                        onClick={tambah}
                        className="flex items-center justify-center my-1 sm:my-0 bg-white text-gray-800 font-semibold py-2 px-4 border border-main-100 hover:bg-main-100 hover:text-white rounded shadow"
                      >
                        Tambah +
                      </button>
                      <button
                        onClick={handleDownloadExcel}
                        className="flex items-center justify-center my-1 sm:my-0 bg-white text-gray-800 font-semibold py-2 px-4 border border-green-600 hover:bg-green-600 hover:text-white rounded shadow"
                      >
                        Download Excel
                      </button>
                      <button
                        onClick={handleDownloadPDF}
                        className="flex items-center justify-center my-1 sm:my-0 bg-white text-gray-800 font-semibold py-2 px-4 border border-red-400 hover:bg-red-400 hover:text-white rounded shadow"
                      >
                        Download PDF
                      </button>
                    </div>
                    <div className="flex flex-row flex-wrap gap-2 items-center">
                      <select
                        value={selectedBagian}
                        onChange={(e) => setSelectedBagian(e.target.value)}
                        className="border border-gray-300 rounded py-2 px-3 text-sm bg-white h-10 min-w-[180px] outline-none"
                      >
                        <option value="">Semua Bagian</option>
                        {bagianInfo.none > 0 && (
                          <option value="__none__">
                            Tanpa Bagian ({bagianInfo.none})
                          </option>
                        )}
                        {bagianInfo.list.map(([nama, c]) => (
                          <option key={nama} value={nama}>
                            {nama} ({c})
                          </option>
                        ))}
                      </select>
                      <div className="w-72">
                        <SearchBarSmallV2
                          text={"Cari ID / Kepada / Keterangan"}
                          value={searchInput}
                          funcs={handleSearchChange}
                        />
                      </div>
                    </div>
                  </div>

                  {/* ── Tabel ── */}
                  <div className="overflow-x-auto px-4 pt-2">
                    <table className="table-auto w-full text-sm text-left text-gray-500 mt-4">
                      <thead className="text-xs text-orange-600 uppercase bg-gray-50">
                        <tr>
                          {colHeader.map((h, i) => (
                            <th key={i} scope="col" className="py-3 px-6">{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {pageRows.length === 0 ? (
                          <tr>
                            <td colSpan={colHeader.length} className="text-center py-8 text-gray-400">
                              {searchQuery ? `Tidak ada data untuk "${searchQuery}"` : "Tidak ada data"}
                            </td>
                          </tr>
                        ) : (
                          pageRows.map((row, id) => (
                            <tr
                              key={row.CashJournalID || id}
                              className={id % 2 === 0 ? "bg-gray-50 border-b" : "bg-white border-b"}
                            >
                              <td className="py-4 px-6 whitespace-nowrap font-mono text-xs">{row.CashJournalID}</td>
                              <td className="py-4 px-6">{row.DariKepada || "-"}</td>
                              <td className="py-4 px-6">{row.NamaBagian || "-"}</td>
                              <td className="py-4 px-6">{row.JenisTransaksi || "-"}</td>
                              <td className="py-4 px-6 whitespace-nowrap">{fmtTgl(row.Tanggal)}</td>
                              <td className="py-4 px-6">{row.Keterangan || "-"}</td>
                              <td className="py-4 px-6 text-right whitespace-nowrap">{fmtRp(row.Nominal)}</td>
                              <td className="py-4 px-6">
                                <span className={statusClass(row.Status)}>{row.Status || "-"}</span>
                              </td>
                              <td className="py-4 px-6 whitespace-nowrap">
                                {row.Status !== "Menunggu Approval" && (
                                  <button
                                    onClick={() => editRow(row.CashJournalID)}
                                    className="border text-white bg-main-100 hover:bg-white hover:border-main-100 hover:text-main-100 font-medium rounded-lg text-sm px-4 py-2 mr-2"
                                  >
                                    Edit
                                  </button>
                                )}
                                {/* "Revisi" = Status 2 = DITOLAK (penamaan lama).
                                    Tanpa memasukkannya ke sini, dokumen yang ditolak
                                    approver bisa diedit tapi tak punya tombol Kirim
                                    sama sekali — mentok permanen. Backend tak
                                    menghalangi: KirimCashJournal hanya memvalidasi isi
                                    jurnal, tidak memeriksa status asal. */}
                                {["Menunggu Dikirim", "Revisi"].includes(row.Status) && (
                                  <button
                                    onClick={() => kirimRow(row.CashJournalID)}
                                    className="border text-white bg-main-100 hover:bg-white hover:border-main-100 hover:text-main-100 font-medium rounded-lg text-sm px-4 py-2"
                                  >
                                    Kirim
                                  </button>
                                )}
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>

                  {/* ── Info + Pagination ── */}
                  <div className="flex flex-row justify-between items-center gap-4 px-4 py-4 text-sm text-gray-600">
                    <span>
                      {filtered.length} data
                      {applied.Bulan
                        ? ` — periode ${new Date(applied.Bulan).toLocaleDateString("id-ID", { month: "long", year: "numeric" })}`
                        : " — semua periode"}
                    </span>
                    <div className="flex items-center gap-4">
                      <button
                        onClick={() => setPage((p) => Math.max(1, p - 1))}
                        disabled={safePage <= 1}
                        className="border px-4 py-1 rounded disabled:opacity-50 hover:bg-gray-100"
                      >
                        Prev
                      </button>
                      <span>Page {safePage} / {totalPage}</span>
                      <button
                        onClick={() => setPage((p) => Math.min(totalPage, p + 1))}
                        disabled={safePage >= totalPage}
                        className="border px-4 py-1 rounded disabled:opacity-50 hover:bg-gray-100"
                      >
                        Next
                      </button>
                    </div>
                  </div>
                </>
              )}
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default KasBankPage;
